import { Css, FormSectionLayout, FormSectionProps, TagGroup, useComputed, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import {
  ConfigureBidGroupsStepQuery,
  useConfigureBidGroupsStepQuery,
} from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BaseBidSection } from "src/routes/bids/plan-based-bid-package/components/BaseBidSection";
import { buildAddOnOptionsSection } from "src/routes/bids/plan-based-bid-package/components/OptionsSection";
import { buildPersonalizationZonesSection } from "src/routes/bids/plan-based-bid-package/components/PersonalizationZonesSection";
import { buildBaseBidCostDimensionsSection } from "src/routes/bids/plan-based-bid-package/components/StandardBaseBidSection";
import { BaseBidType, groupKey, PlanBasedBidPackageFormState } from "src/routes/bids/plan-based-bid-package/utils";
import { queryResult } from "src/utils";

type ConfigureBidGroupsStepProps = { formState: ObjectState<PlanBasedBidPackageFormState> };

export function ConfigureBidGroupsStep(props: ConfigureBidGroupsStepProps) {
  const { formState } = props;
  const productOfferingIds = useComputed(() => formState.productOfferings.value ?? [], [formState.productOfferings]);
  const bidPackageId = useComputed(() => formState.bidPackageId.value ?? "", [formState.bidPackageId]);
  const query = useConfigureBidGroupsStepQuery({
    variables: { bidPackageId, productOfferingIds },
    skip: !bidPackageId || productOfferingIds.isEmpty,
  });

  // A bid can be saved without product offerings. The `bidPackageId` case shouldn't happen (the tab
  // is gated on a saved package), but beats letting the skipped query render blank.
  const placeholder = productOfferingIds.isEmpty
    ? "Select at least one product offering in step 1 to configure bid groups."
    : !bidPackageId
      ? "Saving your progress…"
      : undefined;
  if (placeholder) {
    return <FormSectionLayout title="Configure bid" initialFields={placeholder} />;
  }

  return queryResult(query, (data) => <ConfigureBidGroupsSections formState={formState} {...data} />);
}

type ConfigureBidGroupsSectionsProps = ConfigureBidGroupsStepQuery & {
  formState: ObjectState<PlanBasedBidPackageFormState>;
};

/**
 * Builds `FormSectionLayout`'s `sections` array from the form.
 */
function ConfigureBidGroupsSections(props: ConfigureBidGroupsSectionsProps) {
  const { formState, bidPackage, potentialBidPlanDimensions } = props;
  const { costCodes, costType, projects, productOfferings, potentialBidPlanOptions } = bidPackage;
  const { openModal } = useModal();
  const costCodeNames = costCodes.map((cc) => cc.name).join(", ");
  const baseBidType = useComputed(() => formState.baseBidType.value ?? BaseBidType.Standard, [formState]);
  // FormSectionLayout is not an observer, so rebuild `sections` when a group is added or removed.
  useComputed(() => formState.bidPlanGroups.rows.map((g) => groupKey(g.value)).join(), [formState]);

  const summaryFields = (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.df.aic.gap1.$}>
        <span css={Css.smSb.$}>{costCodeNames}</span>
        <span css={Css.xs.$}> | </span>
        <span css={Css.smSb.$}>{costType.name}</span>
        <span css={Css.xs.$}> | </span>
        <span css={Css.smSb.$}>{projects.map((p) => p.name).join(", ")}</span>
      </div>
      <TagGroup tags={productOfferings.map((pof) => ({ text: pof.name }))} />
    </div>
  );

  let bidTypeDependentSection: FormSectionProps | undefined;
  switch (baseBidType) {
    case BaseBidType.Standard:
      bidTypeDependentSection = buildBaseBidCostDimensionsSection({
        formState,
        dimensionResults: potentialBidPlanDimensions,
        openModal,
      });
      break;
    case BaseBidType.Personalization:
      bidTypeDependentSection = buildPersonalizationZonesSection({
        formState,
        dimensionResults: potentialBidPlanDimensions,
        productOfferings,
        openModal,
      });
      break;
    case BaseBidType.None:
      break;
  }

  const sections: FormSectionProps[] = [
    {
      title: "Base Bid",
      description: "Choose how the base scope of work will be bid. For option-only bids, choose No Base Bid.",
      fields: <BaseBidSection formState={formState} />,
    },
    ...(bidTypeDependentSection ? [bidTypeDependentSection] : []),
    buildAddOnOptionsSection({
      formState,
      dimensionResults: potentialBidPlanDimensions,
      potentialBidPlanOptions,
      openModal,
    }),
  ];

  return (
    <FormSectionLayout
      title={`Configure ${costCodeNames} bid`}
      description={
        "Configure how trades will bid the base scope and add-on options. Your selections create the cost lines in this bid."
      }
      initialFields={summaryFields}
      sections={sections}
    />
  );
}
